import cn from 'clsx'

import React from 'react'
import { useSelector } from 'hooks/use-redux'
import type { Track } from 'types/track'
import { PLAYING } from '@disco/constants/MediaStatus'
import {
  getTrackPlaying,
  selectTrackPlaybackStatus,
} from '@disco/state-management/selectors/media'
import { getFileTypeByExt } from '@disco/utils/file-utils'
import { getTrackArtwork } from '@disco/utils/tracks'
import FileIcon from 'components/common/tracks/track-artwork/file-icon'
import styles from './index.module.css'

/**
 * Player artwork component that displays track artwork with playing animation
 * Shows track artwork image or file type icon with visual feedback for playback status
 * Includes layered images for smooth playing state transitions
 */
const PlayerArtwork = (): null | React.ReactElement<'div'> => {
  const track: Track | null | undefined = useSelector(getTrackPlaying)
  const playbackStatus: string | null | undefined = useSelector(state =>
    track
      ? selectTrackPlaybackStatus(state, {
          trackId: track?.id,
        })
      : null,
  )

  if (!track) {
    return null
  }

  const fileType = getFileTypeByExt(track.ext)
  const backgroundImage = getTrackArtwork(track, 'full')
  return (
    <div
      className={cn(styles.playerArtwork, {
        [styles.playerArtworkPlaying]: playbackStatus === PLAYING,
      })}
    >
      <div
        className={styles.playerArtworkImage}
        style={{
          backgroundImage: backgroundImage
            ? `url(${backgroundImage})`
            : undefined,
        }}
      />
      <div
        className={cn(
          styles.playerArtworkImage,
          styles.playerArtworkImagePlaying,
        )}
        style={{
          backgroundImage: backgroundImage
            ? `url(${backgroundImage})`
            : undefined,
        }}
      />
      {(!backgroundImage || track.virus) && (
        <div className={styles.playerArtworkIcon}>
          <FileIcon fileType={fileType} size="large" virus={track.virus} />
        </div>
      )}
    </div>
  )
}

export default PlayerArtwork
